import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useBaseUrl, postData } from "../../../utils/utils";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import Canvas from "../../layouts/dahboard_canvas";
import { Link, useNavigate } from "react-router-dom";
import localForage from "localforage";
import Swal from "sweetalert2";

/* ─── Types ─── */
interface Patient {
    id: string;
    patient_code: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    gender: string;
    dob: string;
    address: string;
    avatar: string;
    status: string;
    last_visit: string;
    treatments_count: number;
    records_count: number;
    referred_by?: string;
    referring_facility?: string;
    collaboration_title?: string;
    collaboration_id?: number;
}

interface Summary {
    total: number;
    active: number;
    new_this_month: number;
    referred: number;
    collaborative: number;
}

type TabKey = 'my' | 'referred' | 'collaborative';
type ViewMode = 'grid' | 'table';
type ProfileTab = 'overview' | 'ehr' | 'appointments' | 'referrals';

/* ─── Constants ─── */
const PAGE_SIZE = 12;
const ACCENT = '#0f766e';
const ACCENT_STRONG = '#115e59';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.12)';
const ACCENT_BG = 'rgba(15, 118, 110, 0.06)';

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    'linear-gradient(135deg, #059669 0%, #34d399 100%)',
    'linear-gradient(135deg, #0d9488 0%, #5eead4 100%)',
    'linear-gradient(135deg, #047857 0%, #6ee7b7 100%)',
    'linear-gradient(135deg, #115e59 0%, #2dd4bf 100%)',
    'linear-gradient(135deg, #064e3b 0%, #a7f3d0 100%)',
    'linear-gradient(135deg, #0e7490 0%, #67e8f9 100%)',
    'linear-gradient(135deg, #155e75 0%, #22d3ee 100%)',
];

const STATUS_MAP: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    active:       { bg: 'rgba(16,185,129,0.1)', text: '#059669', dot: '#10b981', label: 'Active' },
    inactive:     { bg: 'rgba(244,63,94,0.1)', text: '#e11d48', dot: '#f43f5e', label: 'Inactive' },
    referred:     { bg: 'rgba(59,130,246,0.1)', text: '#2563eb', dot: '#3b82f6', label: 'Referred' },
    discharged:   { bg: 'rgba(245,158,11,0.1)', text: '#d97706', dot: '#f59e0b', label: 'Discharged' },
    collaborative:{ bg: 'rgba(139,92,246,0.1)', text: '#7c3aed', dot: '#8b5cf6', label: 'Collaborative' },
};

/* ─── Helpers ─── */
const ini = (f: string, l: string) => `${(f || '?')[0]}${(l || '?')[0]}`.toUpperCase();
const gradientFor = (s: string) => AVATAR_GRADIENTS[Math.abs(Array.from(s).reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_GRADIENTS.length];
const statusStyle = (s: string) => STATUS_MAP[s] || STATUS_MAP.active;
const calcAge = (dob: string) => dob ? Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000) : null;
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const isValidAvatar = (url: string | undefined) => url && url.length > 5 && !url.includes('avatar-') && (url.startsWith('http') || url.startsWith('/media'));

/* ─── Avatar component with onError fallback ─── */
const PatientAvatar: React.FC<{ patient: Patient; size?: number }> = ({ patient, size = 44 }) => {
    const [imgError, setImgError] = useState(false);
    const grad = gradientFor(patient.first_name + patient.last_name);
    const letters = ini(patient.first_name, patient.last_name);

    if (isValidAvatar(patient.avatar) && !imgError) {
        return (
            <img src={patient.avatar} alt="" onError={() => setImgError(true)}
                style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }} />
        );
    }
    return (
        <div style={{ width: size, height: size, borderRadius: '50%', background: grad, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.36, letterSpacing: 1, border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', flexShrink: 0 }}>
            {letters}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const PatientCenter: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [tab, setTab] = useState<TabKey>('my');
    const [loading, setLoading] = useState(true);
    const [clinicId, setClinicId] = useState<string | null>(null);
    const [myPatients, setMyPatients] = useState<Patient[]>([]);
    const [referredPatients, setReferredPatients] = useState<Patient[]>([]);
    const [collabPatients, setCollabPatients] = useState<Patient[]>([]);
    const [summary, setSummary] = useState<Summary>({ total: 0, active: 0, new_this_month: 0, referred: 0, collaborative: 0 });
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showIdMap, setShowIdMap] = useState<Record<string, boolean>>({});
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState<'name' | 'date' | 'status'>('name');
    const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', phone: '', gender: 'Male', dob: '', address: '' });
    const [creatingPatient, setCreatingPatient] = useState(false);
    const [createFormErrors, setCreateFormErrors] = useState<Record<string, boolean>>({});
    const searchRef = useRef<HTMLInputElement>(null);

    // Inline profile drawer
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [profileTab, setProfileTab] = useState<ProfileTab>('overview');

    /* ─── Load clinic id ─── */
    useEffect(() => {
        document.title = "Patient Center — Uzima";
        (async () => {
            const cid = await localForage.getItem('clinic_id') as string;
            if (cid) setClinicId(cid);
        })();
    }, []);

    /* ─── Fetch patients ─── */
    const loadAll = useCallback(async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('clinic_id', clinicId);

            const [myRes, refRes, colRes, sumRes] = await Promise.allSettled([
                postData(`${baseUrl}facilities/internal-patients`, fd),
                postData(`${baseUrl}facilities/referred-patients`, fd),
                postData(`${baseUrl}facilities/collaborative-patients`, fd),
                postData(`${baseUrl}facilities/patient-summary`, fd),
            ]);

            const parse = (r: PromiseSettledResult<any>) => {
                if (r.status === 'fulfilled' && r.value && Array.isArray(r.value)) return r.value;
                if (r.status === 'fulfilled' && r.value?.data && Array.isArray(r.value.data)) return r.value.data;
                return [];
            };

            const my = parse(myRes);
            const ref = parse(refRes);
            const col = parse(colRes);
            setMyPatients(my);
            setReferredPatients(ref);
            setCollabPatients(col);

            if (sumRes.status === 'fulfilled' && sumRes.value) {
                const s = sumRes.value;
                setSummary({
                    total: s.total || my.length + ref.length + col.length,
                    active: s.active || my.filter((p: Patient) => p.status === 'active').length,
                    new_this_month: s.new_this_month || 0,
                    referred: s.referred || ref.length,
                    collaborative: s.collaborative || col.length,
                });
            } else {
                setSummary({
                    total: my.length + ref.length + col.length,
                    active: my.filter((p: Patient) => p.status === 'active').length,
                    new_this_month: 0,
                    referred: ref.length,
                    collaborative: col.length,
                });
            }
        } catch (e) {
            console.error('Error loading patient data:', e);
        } finally {
            setLoading(false);
        }
    }, [clinicId, baseUrl]);

    useEffect(() => { loadAll(); }, [loadAll]);

    /* ─── Create patient ─── */
    const handleCreatePatient = async () => {
        const { first_name, last_name, email, phone, gender, dob } = createForm;
        const missing: Record<string, boolean> = {
            first_name: !first_name,
            last_name: !last_name,
            email: !email,
            phone: !phone,
            gender: !gender,
            dob: !dob,
        };
        setCreateFormErrors(missing);
        if (Object.values(missing).some(Boolean)) {
            await Swal.fire({ icon: 'warning', title: 'Required Fields', text: 'Please fill in all required fields.' });
            return;
        }
        setCreatingPatient(true);
        try {
            const staffId = await localForage.getItem('staff_id');
            const fd = new FormData();
            fd.append('clinic_id', clinicId || '');
            fd.append('staff_id', String(staffId || ''));
            Object.entries(createForm).forEach(([k, v]) => fd.append(k, v));
            const res = await postData(`${baseUrl}facilities/patient/create`, fd);
            if (res) {
                await Swal.fire({ icon: 'success', title: 'Patient Registered', text: `${first_name} ${last_name} has been added.`, timer: 2000, showConfirmButton: false });
                setShowCreateModal(false);
                setCreateForm({ first_name: '', last_name: '', email: '', phone: '', gender: 'Male', dob: '', address: '' });
                setCreateFormErrors({});
                loadAll();
            }
        } catch (e) {
            console.error('Create patient error:', e);
        } finally {
            setCreatingPatient(false);
        }
    };

    /* ─── Filter + Sort + Paginate ─── */
    const currentList = tab === 'my' ? myPatients : tab === 'referred' ? referredPatients : collabPatients;
    const filtered = currentList
        .filter(p => !search || `${p.first_name} ${p.last_name} ${p.patient_code} ${p.email} ${p.phone}`.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
            if (sortBy === 'name') return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
            if (sortBy === 'date') return (b.last_visit || '').localeCompare(a.last_visit || '');
            return (a.status || '').localeCompare(b.status || '');
        });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    useEffect(() => { setPage(1); }, [tab, search]);

    const toggleId = (id: string) => setShowIdMap(prev => ({ ...prev, [id]: !prev[id] }));

    const openProfile = (p: Patient) => { setSelectedPatient(p); setProfileTab('overview'); };

    /* ─── Pagination ─── */
    const renderPagination = () => {
        if (filtered.length <= PAGE_SIZE) return null;
        const pgs: (number | string)[] = [];
        if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pgs.push(i); }
        else {
            pgs.push(1);
            if (safePage > 3) pgs.push('...');
            for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pgs.push(i);
            if (safePage < totalPages - 2) pgs.push('...');
            pgs.push(totalPages);
        }
        return (
            <div className="sc-card-foot" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>
                    Showing <b>{(safePage - 1) * PAGE_SIZE + 1}</b>–<b>{Math.min(safePage * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} style={{ opacity: safePage <= 1 ? 0.4 : 1 }}>
                        <i className="bx bx-chevron-left"></i>
                    </button>
                    {pgs.map((p, i) =>
                        typeof p === 'string' ? <span key={`e${i}`} style={{ width: 30, textAlign: 'center', color: '#adb5bd', fontSize: '0.8rem' }}>...</span> : (
                            <button key={p} onClick={() => setPage(p)} className={`sc-btn sc-btn-sm ${safePage === p ? 'sc-btn-primary' : 'sc-btn-ghost'}`}
                                style={{ minWidth: 32 }}>{p}</button>
                        )
                    )}
                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} style={{ opacity: safePage >= totalPages ? 0.4 : 1 }}>
                        <i className="bx bx-chevron-right"></i>
                    </button>
                </div>
            </div>
        );
    };

    /* ─── Patient Card (grid mode) ─── */
    const renderPatientCard = (p: Patient) => {
        const st = statusStyle(p.status);
        const a = calcAge(p.dob);
        return (
            <div key={p.id} className="sc-card" onClick={() => openProfile(p)} style={{ cursor: 'pointer' }}>
                <div className="sc-card-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                        <PatientAvatar patient={p} size={48} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="sc-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.first_name} {p.last_name}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-soft)' }}>
                                    {p.gender}{a !== null ? ` · ${a} yrs` : ''}
                                </span>
                                <span className={`sc-badge-status`} style={{ background: st.bg, color: st.text }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, display: 'inline-block', marginRight: 4 }}></span>
                                    {st.label}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14, fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <i className="bx bx-id-card" style={{ width: 16, color: 'var(--uzima-shell-text-muted)' }}></i>
                            <code style={{ fontSize: '0.74rem', background: 'rgba(15,23,42,0.04)', padding: '1px 6px', borderRadius: 4 }}>
                                {showIdMap[p.id] ? (p.patient_code || 'N/A') : '••••••'}
                            </code>
                            <i className={`bx ${showIdMap[p.id] ? 'bx-hide' : 'bx-show'}`}
                                onClick={(e) => { e.stopPropagation(); toggleId(p.id); }}
                                style={{ cursor: 'pointer', color: 'var(--uzima-shell-text-muted)', fontSize: '0.85rem' }}></i>
                        </div>
                        {(p.phone || p.email) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bx bx-phone" style={{ width: 16, color: 'var(--uzima-shell-text-muted)' }}></i>
                                <span>{p.phone || p.email}</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <i className="bx bx-calendar-check" style={{ width: 16, color: 'var(--uzima-shell-text-muted)' }}></i>
                            <span>Last visit: {fmtDate(p.last_visit)}</span>
                        </div>
                        {tab === 'referred' && p.referred_by && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bx bx-building" style={{ width: 16, color: 'var(--uzima-shell-text-muted)' }}></i>
                                <span>From: {p.referred_by || p.referring_facility}</span>
                            </div>
                        )}
                        {tab === 'collaborative' && p.collaboration_title && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7c3aed' }}>
                                <i className="bx bx-link" style={{ width: 16 }}></i>
                                <span style={{ fontWeight: 600 }}>{p.collaboration_title}</span>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <div style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: ACCENT_BG }}>
                            <div style={{ fontSize: '1rem', fontWeight: 800, color: ACCENT }}>{p.treatments_count || 0}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Treatments</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRadius: 8, background: 'rgba(59,130,246,0.06)' }}>
                            <div style={{ fontSize: '1rem', fontWeight: 800, color: '#2563eb' }}>{p.records_count || 0}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Records</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={(e) => { e.stopPropagation(); openProfile(p); }} style={{ flex: 1 }}>
                            <i className="bx bx-user"></i> Profile
                        </button>
                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/institution/ehr/new?patient_id=${p.id}`); }} style={{ flex: 1 }}>
                            <i className="bx bx-file-plus"></i> EHR
                        </button>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={(e) => { e.stopPropagation(); navigate('/calendar'); }} title="Appointments">
                            <i className="bx bx-calendar"></i>
                        </button>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/institution/patient-history/${p.patient_code}`); }} title="Medical History">
                            <i className="bx bx-history"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Table row ─── */
    const renderTableRow = (p: Patient) => {
        const st = statusStyle(p.status);
        const a = calcAge(p.dob);
        return (
            <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openProfile(p)}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(15,118,110,0.02)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <PatientAvatar patient={p} size={38} />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--uzima-shell-text)' }}>{p.first_name} {p.last_name}</div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>{p.gender}{a !== null ? ` · ${a} yrs` : ''}</div>
                        </div>
                    </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <code style={{ fontSize: '0.76rem', background: 'rgba(15,23,42,0.04)', padding: '2px 7px', borderRadius: 4 }}>
                            {showIdMap[p.id] ? (p.patient_code || 'N/A') : '••••••'}
                        </code>
                        <i className={`bx ${showIdMap[p.id] ? 'bx-hide' : 'bx-show'}`}
                            onClick={(e) => { e.stopPropagation(); toggleId(p.id); }}
                            style={{ cursor: 'pointer', fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}></i>
                    </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'var(--uzima-shell-text-soft)' }}>
                    <div>{p.email || '—'}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>{p.phone || '—'}</div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'var(--uzima-shell-text-soft)', whiteSpace: 'nowrap' }}>{fmtDate(p.last_visit)}</td>
                <td style={{ padding: '12px 16px' }}>
                    <span className="sc-badge-status" style={{ background: st.bg, color: st.text }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, display: 'inline-block', marginRight: 4 }}></span>{st.label}
                    </span>
                </td>
                {tab === 'referred' && <td style={{ padding: '12px 16px', fontSize: '0.8rem' }}>{p.referred_by || p.referring_facility || '—'}</td>}
                {tab === 'collaborative' && <td style={{ padding: '12px 16px' }}><span className="sc-badge-status" style={{ background: 'rgba(139,92,246,0.1)', color: '#7c3aed' }}>{p.collaboration_title || `Collab #${p.collaboration_id}`}</span></td>}
                <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ padding: '2px 7px', borderRadius: 6, background: ACCENT_BG, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}><i className="bx bx-capsule me-1"></i>{p.treatments_count || 0}</span>
                        <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(59,130,246,0.06)', color: '#2563eb', fontSize: '0.72rem', fontWeight: 600 }}><i className="bx bx-file me-1"></i>{p.records_count || 0}</span>
                    </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                        <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => openProfile(p)} title="Profile"><i className="bx bx-user"></i></button>
                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => navigate(`/institution/ehr/new?patient_id=${p.id}`)} title="New EHR"><i className="bx bx-file-plus"></i></button>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigate('/calendar')} title="Schedule"><i className="bx bx-calendar"></i></button>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigate(`/institution/patient-history/${p.patient_code}`)} title="Medical History"><i className="bx bx-history"></i></button>
                        {tab === 'my' && <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigate('/institution/create-referral')} title="Refer"><i className="bx bx-send"></i></button>}
                    </div>
                </td>
            </tr>
        );
    };

    /* ─── Patient Profile Drawer ─── */
    const renderProfileDrawer = () => {
        if (!selectedPatient) return null;
        const p = selectedPatient;
        const st = statusStyle(p.status);
        const a = calcAge(p.dob);
        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1055, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setSelectedPatient(null)}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }}></div>
                <div style={{ position: 'relative', width: '100%', maxWidth: 560, background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-8px 0 30px rgba(0,0,0,0.12)', animation: 'slideInRight 0.25s ease-out' }}
                    onClick={e => e.stopPropagation()}>
                    <style>{`@keyframes slideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }`}</style>

                    {/* Drawer header */}
                    <div style={{ background: `linear-gradient(135deg, ${ACCENT_STRONG} 0%, ${ACCENT} 60%, #14b8a6 100%)`, padding: '28px 28px 20px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, position: 'relative', zIndex: 1 }}>
                            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Patient Profile</span>
                            <button onClick={() => setSelectedPatient(null)}
                                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="bx bx-x" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                            <PatientAvatar patient={p} size={64} />
                            <div>
                                <h4 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '1.25rem' }}>{p.first_name} {p.last_name}</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>{p.gender}{a !== null ? ` · ${a} yrs` : ''}</span>
                                    <span style={{ padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '0.7rem', fontWeight: 600 }}>{st.label}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile tabs */}
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--uzima-shell-border)', background: '#fafbfc' }}>
                        {([
                            { key: 'overview' as ProfileTab, label: 'Overview', icon: 'bx-info-circle' },
                            { key: 'ehr' as ProfileTab, label: 'EHR', icon: 'bx-file' },
                            { key: 'appointments' as ProfileTab, label: 'Appointments', icon: 'bx-calendar' },
                            { key: 'referrals' as ProfileTab, label: 'Referrals', icon: 'bx-send' },
                        ]).map(t => (
                            <button key={t.key} onClick={() => setProfileTab(t.key)}
                                style={{ flex: 1, padding: '12px 8px', border: 'none', borderBottom: profileTab === t.key ? `2px solid ${ACCENT}` : '2px solid transparent', background: profileTab === t.key ? '#fff' : 'transparent', color: profileTab === t.key ? ACCENT : 'var(--uzima-shell-text-soft)', fontWeight: profileTab === t.key ? 600 : 400, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 0.15s' }}>
                                <i className={`bx ${t.icon}`} style={{ fontSize: '0.95rem' }}></i> {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Profile content */}
                    <div style={{ padding: '24px 28px' }}>
                        {profileTab === 'overview' && (
                            <>
                                <h6 style={{ fontWeight: 700, marginBottom: 16, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>Patient Information</h6>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                                    {[
                                        { label: 'Patient ID', val: p.patient_code || 'N/A', icon: 'bx-id-card' },
                                        { label: 'Email', val: p.email || '—', icon: 'bx-envelope' },
                                        { label: 'Phone', val: p.phone || '—', icon: 'bx-phone' },
                                        { label: 'Date of Birth', val: p.dob ? fmtDate(p.dob) : '—', icon: 'bx-cake' },
                                        { label: 'Address', val: p.address || '—', icon: 'bx-map' },
                                        { label: 'Last Visit', val: fmtDate(p.last_visit), icon: 'bx-calendar-check' },
                                    ].map((item, i) => (
                                        <div key={i} style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(15,23,42,0.02)', border: '1px solid var(--uzima-shell-border)' }}>
                                            <div style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <i className={`bx ${item.icon}`} style={{ fontSize: '0.82rem' }}></i> {item.label}
                                            </div>
                                            <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--uzima-shell-text)' }}>{item.val}</div>
                                        </div>
                                    ))}
                                </div>

                                <h6 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>Quick Stats</h6>
                                <div className="sc-stats" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 24 }}>
                                    <div className="sc-stat">
                                        <div className="sc-stat-icon teal"><i className="bx bx-capsule"></i></div>
                                        <div className="sc-stat-value">{p.treatments_count || 0}</div>
                                        <div className="sc-stat-label">Treatments</div>
                                    </div>
                                    <div className="sc-stat">
                                        <div className="sc-stat-icon blue"><i className="bx bx-file"></i></div>
                                        <div className="sc-stat-value">{p.records_count || 0}</div>
                                        <div className="sc-stat-label">EHR Records</div>
                                    </div>
                                    <div className="sc-stat">
                                        <div className="sc-stat-icon amber"><i className="bx bx-calendar"></i></div>
                                        <div className="sc-stat-value">—</div>
                                        <div className="sc-stat-label">Appointments</div>
                                    </div>
                                </div>

                                {tab === 'referred' && (p.referred_by || p.referring_facility) && (
                                    <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)', marginBottom: 16 }}>
                                        <div style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 600, marginBottom: 4 }}>REFERRAL INFO</div>
                                        <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text)' }}>Referred by: <b>{p.referred_by || p.referring_facility}</b></div>
                                    </div>
                                )}
                                {tab === 'collaborative' && p.collaboration_title && (
                                    <div style={{ padding: '14px 16px', borderRadius: 10, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)', marginBottom: 16 }}>
                                        <div style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 600, marginBottom: 4 }}>COLLABORATION</div>
                                        <div style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text)' }}>{p.collaboration_title}</div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                    <button className="sc-btn sc-btn-primary" onClick={() => navigate(`/institution/ehr/new?patient_id=${p.id}`)} style={{ flex: 1 }}>
                                        <i className="bx bx-file-plus"></i> Create EHR Record
                                    </button>
                                    <button className="sc-btn sc-btn-outline" onClick={() => navigate('/calendar')} style={{ flex: 1 }}>
                                        <i className="bx bx-calendar-plus"></i> Schedule
                                    </button>
                                </div>
                                <button className="sc-btn sc-btn-ghost" onClick={() => navigate(`/institution/patient-history/${p.patient_code}`)} style={{ width: '100%', marginTop: 10 }}>
                                    <i className="bx bx-history"></i> View Medical History
                                </button>
                                {tab === 'my' && (
                                    <button className="sc-btn sc-btn-ghost" onClick={() => navigate('/institution/create-referral')} style={{ width: '100%', marginTop: 10 }}>
                                        <i className="bx bx-send"></i> Create Referral
                                    </button>
                                )}
                            </>
                        )}

                        {profileTab === 'ehr' && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                    <h6 style={{ fontWeight: 700, margin: 0, color: 'var(--uzima-shell-text)', fontSize: '0.88rem' }}>EHR Records</h6>
                                    <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => navigate(`/institution/ehr/new?patient_id=${p.id}`)}>
                                        <i className="bx bx-plus"></i> New Record
                                    </button>
                                </div>
                                <div style={{ padding: '40px 20px', textAlign: 'center', borderRadius: 12, background: 'rgba(15,23,42,0.02)', border: '1px dashed var(--uzima-shell-border)' }}>
                                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-file" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                    </div>
                                    <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)', marginBottom: 4 }}>{p.records_count || 0} Records</p>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 16 }}>
                                        View and manage medical records for this patient
                                    </p>
                                    <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => navigate(`/institution/ehr?patient_id=${p.id}`)}>
                                        <i className="bx bx-list-ul"></i> View All Records
                                    </button>
                                </div>
                            </>
                        )}

                        {profileTab === 'appointments' && (
                            <div style={{ padding: '40px 20px', textAlign: 'center', borderRadius: 12, background: 'rgba(15,23,42,0.02)', border: '1px dashed var(--uzima-shell-border)' }}>
                                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                    <i className="bx bx-calendar" style={{ fontSize: '1.5rem', color: '#f59e0b' }}></i>
                                </div>
                                <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)', marginBottom: 4 }}>Appointments</p>
                                <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 16 }}>Schedule and manage appointments</p>
                                <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => navigate('/calendar')}>
                                    <i className="bx bx-calendar-plus"></i> Open Calendar
                                </button>
                            </div>
                        )}

                        {profileTab === 'referrals' && (
                            <div style={{ padding: '40px 20px', textAlign: 'center', borderRadius: 12, background: 'rgba(15,23,42,0.02)', border: '1px dashed var(--uzima-shell-border)' }}>
                                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                    <i className="bx bx-send" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
                                </div>
                                <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)', marginBottom: 4 }}>Referrals</p>
                                <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 16 }}>Create and manage patient referrals</p>
                                <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => navigate('/institution/create-referral')}>
                                    <i className="bx bx-send"></i> New Referral
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Create Patient Modal ─── */
    const closeCreateModal = () => {
        if (creatingPatient) return;
        setShowCreateModal(false);
        setCreateFormErrors({});
    };

    const fieldStyle = (key: string): React.CSSProperties =>
        createFormErrors[key] ? { borderColor: '#e11d48', boxShadow: '0 0 0 3px rgba(225,29,72,0.08)' } : {};

    const previewName = `${createForm.first_name} ${createForm.last_name}`.trim();
    const previewAge = calcAge(createForm.dob);

    const renderCreateModal = () => (
        <div className="sc-overlay" onClick={closeCreateModal}>
            <div className="sc-drawer" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
                <div className="sc-drawer-header" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 14, background: ACCENT_SOFT, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="bx bx-user-plus" style={{ fontSize: '1.3rem' }}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ marginBottom: 2 }}>Register New Patient</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)' }}>Add a new patient to your clinic roster</p>
                    </div>
                    <button className="sc-btn sc-btn-ghost" onClick={closeCreateModal}><i className="bx bx-x"></i></button>
                </div>
                <div className="sc-drawer-body" style={{ padding: 28 }}>
                    {previewName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: ACCENT_BG, marginBottom: 22 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: gradientFor(previewName), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.82rem', flexShrink: 0 }}>
                                {ini(createForm.first_name, createForm.last_name)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: 'var(--uzima-shell-text)' }}>{previewName}</div>
                                <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>
                                    {[createForm.gender, previewAge != null ? `${previewAge} yrs` : null].filter(Boolean).join(' · ') || 'Fill in the details below'}
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.35px', color: 'var(--uzima-shell-text-muted)', marginBottom: 10 }}>Identity</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        {[
                            { key: 'first_name', label: 'First Name', type: 'text', required: true, placeholder: 'John' },
                            { key: 'last_name', label: 'Last Name', type: 'text', required: true, placeholder: 'Doe' },
                        ].map(f => (
                            <div key={f.key}>
                                <label className="sc-label">{f.label} <span style={{ color: '#e11d48' }}>*</span></label>
                                <input type={f.type} className="sc-input" style={fieldStyle(f.key)} placeholder={f.placeholder}
                                    value={(createForm as any)[f.key]}
                                    onChange={e => { setCreateForm(prev => ({ ...prev, [f.key]: e.target.value })); setCreateFormErrors(prev => ({ ...prev, [f.key]: false })); }} />
                            </div>
                        ))}
                    </div>

                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.35px', color: 'var(--uzima-shell-text-muted)', margin: '20px 0 10px' }}>Contact &amp; Demographics</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                            <label className="sc-label">Email <span style={{ color: '#e11d48' }}>*</span></label>
                            <input type="email" className="sc-input" style={fieldStyle('email')} placeholder="john@example.com"
                                value={createForm.email}
                                onChange={e => { setCreateForm(prev => ({ ...prev, email: e.target.value })); setCreateFormErrors(prev => ({ ...prev, email: false })); }} />
                        </div>
                        <div>
                            <label className="sc-label">Phone <span style={{ color: '#e11d48' }}>*</span></label>
                            <input type="tel" className="sc-input" style={fieldStyle('phone')} placeholder="+254 7XX XXX XXX"
                                value={createForm.phone}
                                onChange={e => { setCreateForm(prev => ({ ...prev, phone: e.target.value })); setCreateFormErrors(prev => ({ ...prev, phone: false })); }} />
                        </div>
                        <div>
                            <label className="sc-label">Gender <span style={{ color: '#e11d48' }}>*</span></label>
                            <select className="sc-select" style={fieldStyle('gender')} value={createForm.gender} onChange={e => setCreateForm(f => ({ ...f, gender: e.target.value }))}>
                                <option>Male</option><option>Female</option><option>Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="sc-label">Date of Birth <span style={{ color: '#e11d48' }}>*</span></label>
                            <input type="date" className="sc-input" style={fieldStyle('dob')} value={createForm.dob}
                                onChange={e => { setCreateForm(f => ({ ...f, dob: e.target.value })); setCreateFormErrors(prev => ({ ...prev, dob: false })); }} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label className="sc-label">Address</label>
                            <input type="text" className="sc-input" placeholder="City, Country" value={createForm.address} onChange={e => setCreateForm(f => ({ ...f, address: e.target.value }))} />
                        </div>
                    </div>

                    <div style={{ marginTop: 20, padding: '12px 14px', borderRadius: 10, background: ACCENT_BG, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <i className="bx bx-info-circle" style={{ color: ACCENT, fontSize: '1rem' }}></i>
                        <span style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>A unique patient code will be auto-generated. The patient will receive an invitation to access their records.</span>
                    </div>
                </div>
                <div className="sc-drawer-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 28px' }}>
                    <button className="sc-btn sc-btn-ghost" onClick={closeCreateModal} disabled={creatingPatient}>Cancel</button>
                    <button className="sc-btn sc-btn-primary" onClick={handleCreatePatient} disabled={creatingPatient}>
                        {creatingPatient
                            ? <><i className="bx bx-loader-alt bx-spin"></i> Registering...</>
                            : <><i className="bx bx-check"></i> Register Patient</>}
                    </button>
                </div>
            </div>
        </div>
    );

    /* ═══════════ MAIN RENDER ═══════════ */
    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-heart me-2" style={{ color: ACCENT }}></i>Patient Center</h1>
                                <p>Manage patients, referrals, collaborative care &amp; EHR records</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadAll}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/ehr" className="sc-btn sc-btn-outline" style={{ textDecoration: 'none' }}><i className="bx bx-file-find"></i> All EHR</Link>
                                <button className="sc-btn sc-btn-primary" onClick={() => setShowCreateModal(true)}><i className="bx bx-user-plus"></i> New Patient</button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="sc-stats">
                            {[
                                { label: 'Total Patients', val: summary.total, icon: 'bx bx-group', cls: 'teal' },
                                { label: 'Active Now', val: summary.active, icon: 'bx bx-heart', cls: 'blue' },
                                { label: 'New This Month', val: summary.new_this_month, icon: 'bx bx-trending-up', cls: 'amber' },
                                { label: 'Referred', val: summary.referred, icon: 'bx bx-transfer-alt', cls: 'rose' },
                                { label: 'Collaborative', val: summary.collaborative, icon: 'bx bx-network-chart', cls: 'violet' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{loading ? '—' : s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Tabs */}
                        <div className="sc-tabs">
                            {([
                                { key: 'my' as TabKey, label: 'My Patients', icon: 'bx bx-group', count: myPatients.length },
                                { key: 'referred' as TabKey, label: 'Referred', icon: 'bx bx-transfer-alt', count: referredPatients.length },
                                { key: 'collaborative' as TabKey, label: 'Collaborative', icon: 'bx bx-network-chart', count: collabPatients.length },
                            ]).map(t => (
                                <button key={t.key} className={`sc-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                                    <i className={t.icon}></i> {t.label}
                                    {t.count > 0 && <span className="sc-badge">{t.count}</span>}
                                </button>
                            ))}
                        </div>

                        {/* Toolbar */}
                        <div className="sc-toolbar">
                            <div className="sc-search">
                                <i className="bx bx-search"></i>
                                <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, ID, email, phone..." />
                                {search && (
                                    <button onClick={() => { setSearch(''); searchRef.current?.focus(); }}
                                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--uzima-shell-text-muted)', fontSize: '1rem' }}>
                                        <i className="bx bx-x"></i>
                                    </button>
                                )}
                            </div>
                            <select className="sc-select" value={sortBy} onChange={e => setSortBy(e.target.value as any)} style={{ width: 'auto' }}>
                                <option value="name">Sort: Name</option>
                                <option value="date">Sort: Last Visit</option>
                                <option value="status">Sort: Status</option>
                            </select>
                            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--uzima-shell-border)' }}>
                                {(['grid', 'table'] as const).map(m => (
                                    <button key={m} onClick={() => setViewMode(m)}
                                        style={{ width: 36, height: 34, border: 'none', background: viewMode === m ? ACCENT : '#fff', color: viewMode === m ? '#fff' : 'var(--uzima-shell-text-soft)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                                        <i className={`bx ${m === 'grid' ? 'bx-grid-alt' : 'bx-list-ul'}`} style={{ fontSize: '1rem' }}></i>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        {loading ? (
                            <div className="sc-spinner"></div>
                        ) : filtered.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                                <div style={{ width: 72, height: 72, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                                    <i className="bx bx-user-x" style={{ fontSize: '2rem', color: ACCENT }}></i>
                                </div>
                                <h5 style={{ fontWeight: 700, color: 'var(--uzima-shell-text)', marginBottom: 6 }}>No patients found</h5>
                                <p style={{ color: 'var(--uzima-shell-text-muted)', maxWidth: 340, margin: '0 auto 18px', fontSize: '0.86rem' }}>
                                    {search ? 'Try adjusting your search terms' : tab === 'my' ? 'Register your first patient to get started' : 'No patients in this category yet'}
                                </p>
                                {tab === 'my' && !search && (
                                    <button className="sc-btn sc-btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <i className="bx bx-plus"></i> Register Patient
                                    </button>
                                )}
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="sc-card" style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}>
                                <div className="sc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                                    {paginated.map(renderPatientCard)}
                                </div>
                                {renderPagination()}
                            </div>
                        ) : (
                            <div className="sc-card">
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid var(--uzima-shell-border)' }}>
                                                {['Patient', 'ID', 'Contact', 'Last Visit', 'Status',
                                                  ...(tab === 'referred' ? ['Referred By'] : []),
                                                  ...(tab === 'collaborative' ? ['Collaboration'] : []),
                                                  'Records', 'Actions'
                                                ].map((h, i) => (
                                                    <th key={i} style={{ padding: '12px 16px', fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', background: 'rgba(15,23,42,0.02)', whiteSpace: 'nowrap' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>{paginated.map(renderTableRow)}</tbody>
                                    </table>
                                </div>
                                {renderPagination()}
                            </div>
                        )}

                        {/* HIPAA Notice */}
                        <div style={{ marginTop: 20, padding: '12px 20px', borderRadius: 12, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="sc-stat-icon amber" style={{ width: 32, height: 32, borderRadius: 8, marginBottom: 0, flexShrink: 0 }}><i className="bx bx-shield-quarter" style={{ fontSize: '1rem' }}></i></div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>
                                <b style={{ color: '#d97706' }}>HIPAA Compliance</b> — All patient data is protected health information (PHI). Patient IDs are masked by default. Unauthorized access is prohibited.
                            </div>
                        </div>

                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />

            {/* Modals & Drawers */}
            {showCreateModal && renderCreateModal()}
            {renderProfileDrawer()}
        </>
    );
};

export default PatientCenter;
